import { createContext, useCallback, useContext } from 'react';
import type { AnchorId } from './steps';

export type AnchorRegistry = Map<AnchorId, Set<HTMLElement>>;

export type AnchorContextValue = {
  register: (id: AnchorId, element: HTMLElement | null) => void;
  getAnchor: (id: AnchorId) => HTMLElement | null;
  version: number;
};

export const AnchorContext = createContext<AnchorContextValue | null>(null);

// An id is a role, not an element: a target can be claimed by more than one node
// at a time, through a remount or a surface that stays mounted while hidden. The
// coach mark takes the first still-visible claimant in document order rather
// than whichever registered most recently.
export const firstInDocumentOrder = (elements: Set<HTMLElement>) => {
  let first: HTMLElement | null = null;
  elements.forEach((element) => {
    // `inert` marks a surface that has been crossfaded out but left mounted:
    // the page header while customize mode is open still holds the Customize
    // button, and ringing it would trace a control the author cannot see.
    if (!element.isConnected || element.closest('[inert]')) return;
    if (
      !first ||
      first.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_PRECEDING
    ) {
      first = element;
    }
  });

  return first;
};

export const useOnboardingAnchors = () => useContext(AnchorContext);

/**
 * Returns a ref callback a target spreads onto the element the coach mark should
 * point at. Safe to call outside the provider, where it is a no-op, so targets
 * shared with non-planner surfaces stay unconditional.
 */
export const useOnboardingAnchor = (id: AnchorId) => {
  const register = useContext(AnchorContext)?.register;

  return useCallback(
    (element: HTMLElement | null) => register?.(id, element),
    [register, id]
  );
};
